import React from "react";
import "./Roulette.css";

type Props = {
  initialIndex: number;
  options: string[];
  onChange: (optionIndex: number, option: string) => void;
};

const DEG_PER_OPTION = 32;
const INERTIA = 0.88;
const STOP_OMEGA = 0.06;
const VISIBLE_RANGE = 3;

export const Roulette: React.FC<Props> = ({
  initialIndex,
  options,
  onChange,
}) => {
  const [angle, setAngle] = React.useState(0);
  const [omega, setOmega] = React.useState(0);

  const draggingRef = React.useRef(false);
  const lastMouseYRef = React.useRef(0);
  const lastEmittedRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    let raf: number;

    const loop = () => {
      if (!draggingRef.current) {
        const snapAngle = Math.round(angle / DEG_PER_OPTION) * DEG_PER_OPTION;

        let newOmega = omega * INERTIA;
        let newAngle = angle + newOmega;

        if (Math.abs(newOmega) < STOP_OMEGA) {
          newOmega = 0;
          newAngle = snapAngle;
        }

        setOmega(newOmega);
        setAngle(newAngle);
      }

      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [angle, omega]);

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    lastMouseYRef.current = e.clientY;
    setOmega(0);
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!draggingRef.current) return;

    const dy = e.clientY - lastMouseYRef.current;
    lastMouseYRef.current = e.clientY;

    const deltaAngle = dy * 0.6;
    setAngle((a) => a + deltaAngle);
    setOmega(deltaAngle * 1.8);
  };

  const onMouseUp = () => {
    draggingRef.current = false;
  };

  const total = options.length;

  const floatIndex = initialIndex - angle / DEG_PER_OPTION;
  const centerIndex = Math.round(floatIndex);
  const offset = floatIndex - centerIndex;

  const currentOption = ((centerIndex % total) + total) % total;

  React.useEffect(() => {
    if (omega === 0) {
      if (lastEmittedRef.current !== currentOption) {
        lastEmittedRef.current = currentOption;
        onChange(currentOption, options[currentOption]);
      }
    }
  }, [omega, currentOption, options, onChange]);

  const rendered = [];

  for (let i = -VISIBLE_RANGE; i <= VISIBLE_RANGE; i++) {
    const wrappedIndex = (((centerIndex + i) % total) + total) % total;

    const delta = (i - offset) * DEG_PER_OPTION;

    const translateY = delta * 1.1;
    const rotateX = delta * 0.7;
    const depth = Math.min(160, Math.abs(delta) * 1.8);
    const translateZ = -depth;

    const scale = Math.max(0.65, 1 - Math.abs(delta) / 140);
    const opacity = Math.max(0.25, 1 - Math.abs(delta) / 100);

    rendered.push(
      <div
        key={`${wrappedIndex}-${i}`}
        style={{
          position: "absolute",
          transform: `
            translateY(${translateY}px)
            translateZ(${translateZ}px)
            rotateX(${rotateX}deg)
            scale(${scale})
          `,
          opacity,
          fontWeight: wrappedIndex === currentOption ? 700 : 400,
          transformStyle: "preserve-3d",
          willChange: "transform",
          transition: draggingRef.current ? "none" : "transform 0.18s ease-out",
        }}
      >
        {options[wrappedIndex]}
      </div>,
    );
  }

  return (
    <div
      className="rouletteContainer"
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
    >
      {rendered}
    </div>
  );
};
